import { Component, ChangeDetectionStrategy, inject, signal, computed, Signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { toSignal } from '@angular/core/rxjs-interop';
import { Auth } from '../../../services/auth';
import { ToastrService } from 'ngx-toastr';

// Constants
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REGEX = {
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  NUMBER: /\d/,
  SPECIAL_CHAR: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/
};

// Interfaces
interface PasswordFormData {
  newPassword: FormControl<string | null>;
  confirmPassword: FormControl<string | null>;
}

interface PasswordRequirements {
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPassword implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(Auth);
  private toastr = inject(ToastrService);

  // Route parameter
  private code = '';

  // UI State
  hideNewPassword = signal(true);
  hideConfirmPassword = signal(true);
  isLoading = signal(false);

  // Form
  passwordForm!: FormGroup<PasswordFormData>;

  // Reactive Signals
  newPasswordValue!: Signal<string | null>;
  confirmPasswordValue!: Signal<string | null>;
  passwordRequirements!: Signal<PasswordRequirements>;
  passwordsMatch!: Signal<boolean>;

  constructor() {
    this.initializeForm();
    this.initializeSignals();
  }

  ngOnInit(): void {
    this.code = this.route.snapshot.paramMap.get('code') || '';
    if (!this.code) {
      this.toastr.error('Hibás vagy hiányzó kód!', 'Hiba');
      this.router.navigate(['/']);
    }
  }

  private initializeForm(): void {
    this.passwordForm = this.fb.group({
      newPassword: ['', [Validators.required, this.passwordStrengthValidator.bind(this)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator.bind(this) });
  }

  private initializeSignals(): void {
    this.newPasswordValue = toSignal(
      this.passwordForm.get('newPassword')!.valueChanges,
      { initialValue: '' }
    );
    this.confirmPasswordValue = toSignal(
      this.passwordForm.get('confirmPassword')!.valueChanges,
      { initialValue: '' }
    );

    this.passwordRequirements = computed(() => {
      const newPassword = this.newPasswordValue() || '';
      return {
        minLength: newPassword.length >= PASSWORD_MIN_LENGTH,
        hasUpperCase: PASSWORD_REGEX.UPPERCASE.test(newPassword),
        hasLowerCase: PASSWORD_REGEX.LOWERCASE.test(newPassword),
        hasNumber: PASSWORD_REGEX.NUMBER.test(newPassword),
        hasSpecialChar: PASSWORD_REGEX.SPECIAL_CHAR.test(newPassword)
      };
    });

    this.passwordsMatch = computed(() => {
      const newPassword = this.newPasswordValue() || '';
      const confirmPassword = this.confirmPasswordValue() || '';
      return !!(newPassword && confirmPassword && newPassword === confirmPassword);
    });
  }

  private passwordStrengthValidator(control: AbstractControl) {
    const password = control.value;
    if (!password) return null;

    const hasMinLength = password.length >= PASSWORD_MIN_LENGTH;
    const hasUpperCase = PASSWORD_REGEX.UPPERCASE.test(password);
    const hasLowerCase = PASSWORD_REGEX.LOWERCASE.test(password);
    const hasNumber = PASSWORD_REGEX.NUMBER.test(password);
    const hasSpecialChar = PASSWORD_REGEX.SPECIAL_CHAR.test(password);

    const isValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
    return isValid ? null : { passwordStrength: true };
  }

  private passwordMatchValidator(group: AbstractControl) {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (!newPassword || !confirmPassword) return null;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  onSubmit(): void {
    if (this.isLoading()) return;
    if (!this.passwordForm.valid) {
      this.markAllFieldsAsTouched();
      return;
    }

    this.isLoading.set(true);

    this.authService.changePassword({
      identifier: null,
      oldPass: this.code,
      newPass: this.passwordForm.value.newPassword!
    }, true).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.toastr.success('Jelszó sikeresen megváltoztatva!', 'Sikeres művelet');
        this.router.navigate(['/']);
      },
      error: (error) => {
        this.isLoading.set(false);
        this.toastr.error(error.error?.message || 'Hiba történt a jelszó megváltoztatása során!', 'Hiba');
      }
    });
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.passwordForm.controls).forEach(key => {
      this.passwordForm.get(key)?.markAsTouched();
    });
  }
}
